import React, { useState, useEffect } from "react";
import { Backdrop, Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import { FormControl, TextField } from "@mui/material";

const PAYMENT_FORM_ID = "payment-form";

export default function XplorPaymentElement(props) {
  const { amount, disabled, validate, showPolicy, publicKey, onTokenSuccess } = props;

  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  const [zip, setZip] = useState("");

  useEffect(() => {
    if (typeof window.ClearentSDK !== "undefined") {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `${process.env.REACT_APP_XPLOR_BASE_URL}/js-sdk/js/clearent-host.js`;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setError("Payment form failed to load. Please refresh the page.");
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // After script load: poll for ClearentSDK (it may appear async), then init when we have publicKey. Div#payment-form is rendered when scriptLoaded so it exists for init().
  useEffect(() => {
    if (!scriptLoaded || !publicKey) return;

    if (
      typeof window.ClearentSDK !== "undefined" &&
      typeof window.ClearentSDK.reset === "function"
    ) {
      try {
        window.ClearentSDK.reset();
      } catch (_) {
        /* ignore */
      }
    }
    setSdkReady(false);

    let initSucceeded = false;

    const tryInit = () => {
      if (typeof window.ClearentSDK === "undefined") return false;
      try {
        window.ClearentSDK.init({
          pk: publicKey,
          paymentFormId: PAYMENT_FORM_ID,
          successCallback: "ClearentTokenSuccess",
          errorCallback: "ClearentTokenError",
          cardMasked: false,
          cvcMasked: false,
          expDateMasked: false,
          styles: `#card-group.has-error #card{ color: #c0392b }#exp-date-group.has-error{border-color: #c0392b !important;}#card-group.has-error{border: 0}.form-control{padding: 25px;`,
        });
        initSucceeded = true;
        setSdkReady(true);
        return true;
      } catch (err) {
        setError("Payment configuration error. Please refresh the page.");
        return true;
      }
    };

    if (tryInit()) return;

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds
    const interval = setInterval(() => {
      attempts++;
      if (tryInit()) {
        clearInterval(interval);
        return;
      }
      if (attempts >= maxAttempts && !initSucceeded) {
        clearInterval(interval);
        setError("Payment SDK failed to initialize. Please refresh the page.");
      }
    }, 100);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initOptions merged at init time only
  }, [scriptLoaded, publicKey]);

  // Cleanup: destroy SDK iframe on unmount so init can be called again if component remounts
  useEffect(() => {
    return () => {
      if (
        typeof window.ClearentSDK !== "undefined" &&
        typeof window.ClearentSDK.reset === "function"
      ) {
        window.ClearentSDK.reset();
      }
    };
  }, []);

  // Global callbacks for getPaymentToken() response - see ClearentTokenSuccess / ClearentTokenError in SDK docs
  useEffect(() => {
    const handleTokenSuccess = (raw, json) => {
      setSucceeded(true);
      if (onTokenSuccess && json) {
        onTokenSuccess(json?.payload?.["mobile-jwt"], zip);
      }
      setProcessing(false);
    };

    const handleTokenError = (raw, json) => {
      setProcessing(false);
      const msg =
        json?.payload?.["display-message"] ||
        json?.message ||
        (typeof raw === "string" && raw.includes("merchant key")
          ? "Invalid merchant key. Use a sandbox public key with the sandbox script (gateway-sb.clearent.net) or production key with production."
          : "Card could not be processed. Please check your details and try again.");
      setError(typeof msg === "string" ? msg : "Payment failed. Please try again.");
    };

    window.ClearentTokenSuccess = handleTokenSuccess;
    window.ClearentTokenError = handleTokenError;
    return () => {
      delete window.ClearentTokenSuccess;
      delete window.ClearentTokenError;
    };
  }, [onTokenSuccess]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!sdkReady || processing || disabled || typeof window.ClearentSDK === "undefined") {
      return;
    }

    const isValid = await validate();
    if (!isValid) {
      return;
    }

    setError(null);
    setProcessing(true);

    // getPaymentToken() calls successCallback (ClearentTokenSuccess) or errorCallback (ClearentTokenError) - see SDK docs
    window.ClearentSDK.getPaymentToken();
  };

  const isButtonDisabled = processing || disabled || !sdkReady;

  return (
    <form id="xplor-payment-form" onSubmit={handleSubmit}>
      <Box mb={4}>
        {!scriptLoaded ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress size={24} />
            <Typography variant="caption" sx={{ ml: 1 }}>
              Loading payment form...
            </Typography>
          </Box>
        ) : (
          <>
            {!sdkReady && (
              <Box display="flex" justifyContent="center" alignItems="center" height="80px">
                <CircularProgress size={24} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Initializing...
                </Typography>
              </Box>
            )}
            <div id={PAYMENT_FORM_ID} style={{ minHeight: sdkReady ? "auto" : 0 }} />
            <FormControl variant="outlined" fullWidth>
              <TextField
                value={zip}
                variant="outlined"
                fullWidth
                id="zip"
                label="Zip Code"
                name="zip"
                onChange={(e) => setZip(e.target.value)}
                required
              />
            </FormControl>
          </>
        )}
      </Box>
      {showPolicy && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            By selecting the button below, I agree to all the{" "}
            <Link underline="always" href="/store-policy">
              Store Policies
            </Link>
            ,{" "}
            <Link underline="always" href="https://bakestreet.co/terms-of-service">
              Terms of Service
            </Link>{" "}
            and acknowledge the{" "}
            <Link underline="always" href="https://bakestreet.co/privacy-policy">
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>
      )}
      {error && (
        <Box align="center" my={1}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      <Button
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        fullWidth
        disabled={isButtonDisabled}
      >
        {processing ? "Processing..." : `Pay ${amount}`}
      </Button>
      {processing && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </form>
  );
}
